/*
Copyright 2026 Chain Care.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { logger as rootLogger } from "matrix-js-sdk/lib/logger";

import type { PrenigmaCollaborationRuntimeSourceEventDelivery } from "./collaborationRuntimeBootstrap.ts";
import type {
  PrenigmaStreamingVoiceEvidenceRequest,
  PrenigmaStreamingVoiceEvidenceRuntimeHost,
} from "./streamingVoiceEvidenceBridge.ts";
import {
  PRENIGMA_CONTINUOUS_CALL_MEDIA_EVIDENCE_SOURCE,
  type PrenigmaStreamingVoiceBargeInReadback,
  type PrenigmaContinuousMediaObservationReadback,
  type PrenigmaStreamingVoiceProviderResolution,
  type PrenigmaStreamingVoiceRoomContext,
} from "./streamingVoiceTurnEvidence.ts";
import type { PrenigmaTtsPlaybackAcknowledgement } from "./ttsPlaybackBridge.ts";

const logger = rootLogger.getChild("[PrenigmaContinuousVoiceProducerBridge]");

type Unsubscribe = () => void;

export interface PrenigmaContinuousVoiceProducerContext {
  productId?: string;
  roomId?: string;
  sessionId?: string;
  tenantId?: string;
  workspaceId?: string;
  workflowId?: string;
}

export interface PrenigmaContinuousVoiceStreamingSttPartialObservation {
  finalAtMs: number;
  firstPartialAtMs: number;
  providerResolution: PrenigmaStreamingVoiceProviderResolution;
  requestId?: string;
  startedAtMs: number;
  turnId: string;
}

export interface PrenigmaContinuousVoiceIncrementalAgentDispatchObservation {
  ackAtMs: number;
  finalAtMs: number;
  matrixTimelineReadbackVerified: true;
  roomContext: PrenigmaStreamingVoiceRoomContext;
  sourceEventReadbackVerified: true;
  turnId: string;
}

export interface PrenigmaContinuousVoiceStreamingTtsAudioObservation {
  bargeIn?: PrenigmaStreamingVoiceBargeInReadback;
  completedAtMs: number;
  firstAudioAtMs: number;
  playbackRequestId: string;
  providerResolution: PrenigmaStreamingVoiceProviderResolution;
  turnId: string;
}

export interface PrenigmaContinuousVoiceProducerRuntimeHost {
  acknowledgeContinuousVoiceProducerRequest?: (
    acknowledgement: PrenigmaContinuousVoiceProducerAcknowledgement,
  ) => void;
  onIncrementalAgentDispatchObserved?: (
    handler: (
      observation: PrenigmaContinuousVoiceIncrementalAgentDispatchObservation,
    ) => void,
  ) => Unsubscribe | void;
  onStreamingSttPartialObserved?: (
    handler: (
      observation: PrenigmaContinuousVoiceStreamingSttPartialObservation,
    ) => void,
  ) => Unsubscribe | void;
  onStreamingTtsAudioObserved?: (
    handler: (
      observation: PrenigmaContinuousVoiceStreamingTtsAudioObservation,
    ) => void,
  ) => Unsubscribe | void;
  readPendingIncrementalAgentDispatchObservations?: () => PrenigmaContinuousVoiceIncrementalAgentDispatchObservation[];
  readPendingStreamingSttPartialObservations?: () => PrenigmaContinuousVoiceStreamingSttPartialObservation[];
  readPendingStreamingTtsAudioObservations?: () => PrenigmaContinuousVoiceStreamingTtsAudioObservation[];
}

export type PrenigmaContinuousVoiceProducerAcknowledgementStatus =
  | "invalid_observation"
  | "request_emitted"
  | "streaming_evidence_host_unavailable";

export interface PrenigmaContinuousVoiceProducerAcknowledgement {
  blockers: string[];
  providerCredentialsReturned: false;
  rawAudioReturned: false;
  rawSecretMaterialReturned: false;
  rawTranscriptReturned: false;
  requestId?: string;
  status: PrenigmaContinuousVoiceProducerAcknowledgementStatus;
  turnId: string;
}

export interface ConnectPrenigmaContinuousVoiceProducerBridgeOptions {
  context?: PrenigmaContinuousVoiceProducerContext;
  host?: PrenigmaContinuousVoiceProducerRuntimeHost;
  requireTtsPlaybackAcknowledgement?: boolean;
  streamingVoiceEvidenceHost?: PrenigmaStreamingVoiceEvidenceRuntimeHost;
}

export interface PrenigmaContinuousVoiceProducerBridge {
  disconnect: () => void;
  observeSourceEventDelivery: (
    delivery: PrenigmaCollaborationRuntimeSourceEventDelivery,
  ) => void;
  observeTtsPlaybackAcknowledgement: (
    acknowledgement: PrenigmaTtsPlaybackAcknowledgement,
  ) => void;
}

interface ContinuousMediaSourceProof {
  bridgeMetrics: {
    eventSourceToBridgeIngestMs: number;
    eventSourceToMatrixPersistMs: number;
  };
  observation: PrenigmaContinuousMediaObservationReadback;
}

interface TurnState {
  dispatch?: PrenigmaContinuousVoiceIncrementalAgentDispatchObservation;
  emittedRequestId?: string;
  stt?: PrenigmaContinuousVoiceStreamingSttPartialObservation;
  tts?: PrenigmaContinuousVoiceStreamingTtsAudioObservation;
}

const RAW_MATERIAL_KEYS = new Set([
  "api_key",
  "audio",
  "audio_base64",
  "audio_bytes",
  "authorization",
  "credentials",
  "matrix_event_content",
  "provider_secret",
  "raw_audio",
  "raw_event_content",
  "raw_secret_material",
  "raw_transcript",
  "secret",
  "source_event_content",
  "text",
  "token",
  "transcript",
  "transcript_text",
]);

export function connectPrenigmaContinuousVoiceProducerBridge({
  context = {},
  host,
  requireTtsPlaybackAcknowledgement = true,
  streamingVoiceEvidenceHost,
}: ConnectPrenigmaContinuousVoiceProducerBridgeOptions = {}): PrenigmaContinuousVoiceProducerBridge {
  let connected = true;
  let continuousMediaSourceProof: ContinuousMediaSourceProof | undefined =
    undefined;
  const ttsPlaybackAcknowledgements = new Map<
    string,
    PrenigmaTtsPlaybackAcknowledgement
  >();
  const turns = new Map<string, TurnState>();

  const subscriptions: Unsubscribe[] = [];

  const observeStt = (
    observation: PrenigmaContinuousVoiceStreamingSttPartialObservation,
  ): void => {
    if (!connected) return;
    if (!safeObservation(observation, "stt")) {
      acknowledge(host, {
        ...baseAcknowledgement(observation?.turnId ?? ""),
        blockers: ["streaming_stt_partial_observation_invalid"],
        status: "invalid_observation",
      });
      return;
    }
    const turnId = safeText(observation.turnId);
    if (!turnId) return;
    turnState(turns, turnId).stt = observation;
    maybeEmitStreamingVoiceEvidenceRequest({
      context,
      continuousMediaSourceProof,
      host,
      requireTtsPlaybackAcknowledgement,
      streamingVoiceEvidenceHost,
      ttsPlaybackAcknowledgements,
      turnId,
      turns,
    });
  };

  const observeDispatch = (
    observation: PrenigmaContinuousVoiceIncrementalAgentDispatchObservation,
  ): void => {
    if (!connected) return;
    if (!safeObservation(observation, "agent_dispatch")) {
      acknowledge(host, {
        ...baseAcknowledgement(observation?.turnId ?? ""),
        blockers: ["incremental_agent_dispatch_observation_invalid"],
        status: "invalid_observation",
      });
      return;
    }
    const turnId = safeText(observation.turnId);
    if (!turnId) return;
    turnState(turns, turnId).dispatch = observation;
    maybeEmitStreamingVoiceEvidenceRequest({
      context,
      continuousMediaSourceProof,
      host,
      requireTtsPlaybackAcknowledgement,
      streamingVoiceEvidenceHost,
      ttsPlaybackAcknowledgements,
      turnId,
      turns,
    });
  };

  const observeTts = (
    observation: PrenigmaContinuousVoiceStreamingTtsAudioObservation,
  ): void => {
    if (!connected) return;
    if (!safeObservation(observation, "tts")) {
      acknowledge(host, {
        ...baseAcknowledgement(observation?.turnId ?? ""),
        blockers: ["streaming_tts_audio_observation_invalid"],
        status: "invalid_observation",
      });
      return;
    }
    const turnId = safeText(observation.turnId);
    if (!turnId) return;
    turnState(turns, turnId).tts = observation;
    maybeEmitStreamingVoiceEvidenceRequest({
      context,
      continuousMediaSourceProof,
      host,
      requireTtsPlaybackAcknowledgement,
      streamingVoiceEvidenceHost,
      ttsPlaybackAcknowledgements,
      turnId,
      turns,
    });
  };

  drainPending(host?.readPendingStreamingSttPartialObservations, observeStt);
  drainPending(
    host?.readPendingIncrementalAgentDispatchObservations,
    observeDispatch,
  );
  drainPending(host?.readPendingStreamingTtsAudioObservations, observeTts);
  subscribe(subscriptions, host?.onStreamingSttPartialObserved, observeStt);
  subscribe(
    subscriptions,
    host?.onIncrementalAgentDispatchObserved,
    observeDispatch,
  );
  subscribe(subscriptions, host?.onStreamingTtsAudioObserved, observeTts);

  const bridge: PrenigmaContinuousVoiceProducerBridge = {
    disconnect: (): void => {
      connected = false;
      for (const unsubscribe of subscriptions.splice(0)) {
        unsubscribe();
      }
      turns.clear();
      ttsPlaybackAcknowledgements.clear();
      continuousMediaSourceProof = undefined;
    },
    observeSourceEventDelivery: (
      delivery: PrenigmaCollaborationRuntimeSourceEventDelivery,
    ): void => {
      if (!connected) return;
      const sourceProof = continuousMediaSourceProofFromDelivery(delivery);
      if (sourceProof === undefined) return;
      continuousMediaSourceProof = sourceProof;
      for (const turnId of turns.keys()) {
        maybeEmitStreamingVoiceEvidenceRequest({
          context,
          continuousMediaSourceProof,
          host,
          requireTtsPlaybackAcknowledgement,
          streamingVoiceEvidenceHost,
          ttsPlaybackAcknowledgements,
          turnId,
          turns,
        });
      }
    },
    observeTtsPlaybackAcknowledgement: (
      acknowledgement: PrenigmaTtsPlaybackAcknowledgement,
    ): void => {
      if (!connected) return;
      const requestId = safeText(acknowledgement.requestId);
      if (!requestId) return;
      ttsPlaybackAcknowledgements.set(requestId, acknowledgement);
      for (const [turnId, state] of turns.entries()) {
        if (state.tts?.playbackRequestId === requestId) {
          maybeEmitStreamingVoiceEvidenceRequest({
            context,
            continuousMediaSourceProof,
            host,
            requireTtsPlaybackAcknowledgement,
            streamingVoiceEvidenceHost,
            ttsPlaybackAcknowledgements,
            turnId,
            turns,
          });
        }
      }
    },
  };

  return bridge;
}

function maybeEmitStreamingVoiceEvidenceRequest({
  context,
  continuousMediaSourceProof,
  host,
  requireTtsPlaybackAcknowledgement,
  streamingVoiceEvidenceHost,
  ttsPlaybackAcknowledgements,
  turnId,
  turns,
}: {
  context: PrenigmaContinuousVoiceProducerContext;
  continuousMediaSourceProof: ContinuousMediaSourceProof | undefined;
  host: PrenigmaContinuousVoiceProducerRuntimeHost | undefined;
  requireTtsPlaybackAcknowledgement: boolean;
  streamingVoiceEvidenceHost:
    | PrenigmaStreamingVoiceEvidenceRuntimeHost
    | undefined;
  ttsPlaybackAcknowledgements: Map<string, PrenigmaTtsPlaybackAcknowledgement>;
  turnId: string;
  turns: Map<string, TurnState>;
}): void {
  const state = turns.get(turnId);
  if (
    state === undefined ||
    state.emittedRequestId !== undefined ||
    state.stt === undefined ||
    state.dispatch === undefined ||
    state.tts === undefined ||
    continuousMediaSourceProof === undefined
  ) {
    return;
  }

  if (!allContextPresent(context)) return;

  const playbackAcknowledgement = ttsPlaybackAcknowledgements.get(
    state.tts.playbackRequestId,
  );
  if (
    requireTtsPlaybackAcknowledgement &&
    !ttsPlaybackAcknowledgementProved(playbackAcknowledgement)
  ) {
    return;
  }

  if (
    streamingVoiceEvidenceHost?.requestStreamingVoiceTurnEvidence === undefined
  ) {
    acknowledge(host, {
      ...baseAcknowledgement(turnId),
      blockers: ["streaming_voice_evidence_host_request_api_missing"],
      status: "streaming_evidence_host_unavailable",
    });
    return;
  }

  const requestId =
    safeText(state.stt.requestId) ??
    `continuous_voice_turn_${safeRefSegment(turnId)}`;
  const request: PrenigmaStreamingVoiceEvidenceRequest = {
    agentAckAtMs: state.dispatch.ackAtMs,
    agentFinalAtMs: state.dispatch.finalAtMs,
    ...(state.tts.bargeIn !== undefined ? { bargeIn: state.tts.bargeIn } : {}),
    bridgeMetrics: continuousMediaSourceProof.bridgeMetrics,
    continuousMediaObservation: continuousMediaSourceProof.observation,
    evidenceProvenance: {
      browserHarness: false,
      continuousCallMediaObserved: true,
      derivedFromBatchVoiceTurn: false,
      hostSlot: "streamingVoiceEvidence",
      producer: "element_call_collaboration_runtime",
      sourceKind: PRENIGMA_CONTINUOUS_CALL_MEDIA_EVIDENCE_SOURCE,
      sourceOfTruth: "MatrixRTC live media producer",
    },
    liveProof: {
      authenticatedLiveSource: true,
      incrementalAgentDispatchObserved: true,
      matrixTimelineReadbackVerified: true,
      sourceEventReadbackVerified: true,
      streamingSttPartialObserved: true,
      streamingTtsAudioObserved: true,
    },
    productId: context.productId,
    providerResolution: {
      stt: state.stt.providerResolution,
      tts: state.tts.providerResolution,
    },
    requestId,
    roomContext: state.dispatch.roomContext,
    roomId: context.roomId,
    sessionId: context.sessionId,
    startedAtMs: state.stt.startedAtMs,
    sttFinalAtMs: state.stt.finalAtMs,
    sttFirstPartialAtMs: state.stt.firstPartialAtMs,
    tenantId: context.tenantId,
    ttsCompletedAtMs: state.tts.completedAtMs,
    ttsFirstAudioAtMs: state.tts.firstAudioAtMs,
    turnId,
    workspaceId: context.workspaceId,
    workflowId: context.workflowId,
  };

  try {
    assertNoRawMaterial(request, "request");
    streamingVoiceEvidenceHost.requestStreamingVoiceTurnEvidence(request);
    state.emittedRequestId = requestId;
    acknowledge(host, {
      ...baseAcknowledgement(turnId),
      blockers: [],
      requestId,
      status: "request_emitted",
    });
  } catch (error) {
    logger.warn("Prenigma continuous voice producer request emission failed", {
      error,
      requestId,
      turnId,
    });
    acknowledge(host, {
      ...baseAcknowledgement(turnId),
      blockers: ["streaming_voice_evidence_request_emission_failed"],
      requestId,
      status: "invalid_observation",
    });
  }
}

function continuousMediaSourceProofFromDelivery(
  delivery: PrenigmaCollaborationRuntimeSourceEventDelivery,
): ContinuousMediaSourceProof | undefined {
  if (
    delivery.accepted !== true ||
    delivery.sourceEventReadbackVerified !== true ||
    delivery.matrixTimelineReadbackVerified !== true ||
    delivery.sourceEnvelopeSignatureVerified !== true ||
    !continuousMediaObservationProved(delivery.continuousMediaObservation)
  ) {
    return undefined;
  }
  const occurredAtMs = Date.parse(delivery.event.occurred_at);
  const receivedAtMs = Number.isFinite(delivery.receivedAtMs)
    ? delivery.receivedAtMs
    : Date.now();
  const elapsed = Number.isFinite(occurredAtMs)
    ? Math.max(0, receivedAtMs - occurredAtMs)
    : 0;
  return {
    bridgeMetrics: {
      eventSourceToBridgeIngestMs: elapsed,
      eventSourceToMatrixPersistMs: elapsed,
    },
    observation: delivery.continuousMediaObservation,
  };
}

function continuousMediaObservationProved(
  observation: PrenigmaContinuousMediaObservationReadback | undefined,
): observation is PrenigmaContinuousMediaObservationReadback {
  return Boolean(
    observation !== undefined &&
    observation.status === "proved" &&
    observation.proved === true &&
    observation.evidenceSourceKind ===
      PRENIGMA_CONTINUOUS_CALL_MEDIA_EVIDENCE_SOURCE &&
    observation.continuousCallMediaObserved === true &&
    observation.sourceEventReadbackVerified === true &&
    observation.sourceEnvelopeSignatureVerified === true &&
    safeText(observation.sourceEventRef) !== undefined &&
    safeText(observation.receiptEventId) !== undefined &&
    observation.hostSlot === "streamingVoiceEvidence" &&
    observation.producer === "element_call_collaboration_runtime" &&
    observation.mediaKind === "audio" &&
    observation.mediaRuntime === "livekit" &&
    observation.trackSource === "microphone" &&
    safeText(observation.trackReadyState) !== undefined &&
    safeText(observation.trackReadyState)?.toLowerCase() !== "ended" &&
    observation.browserHarness === false &&
    observation.derivedFromBatchVoiceTurn === false &&
    observation.rawAudioReturned === false &&
    observation.rawTranscriptReturned === false &&
    observation.providerCredentialsReturned === false &&
    observation.rawEventContentReturned === false &&
    observation.rawSecretMaterialReturned === false &&
    observation.blockers.length === 0,
  );
}

function ttsPlaybackAcknowledgementProved(
  acknowledgement: PrenigmaTtsPlaybackAcknowledgement | undefined,
): boolean {
  return Boolean(
    acknowledgement !== undefined &&
    acknowledgement.status === "playing" &&
    acknowledgement.playInvoked === true &&
    acknowledgement.playResolved === true &&
    acknowledgement.registered === true &&
    acknowledgement.audioSourceReturned === false &&
    acknowledgement.providerCredentialsReturned === false &&
    acknowledgement.rawAudioReturned === false &&
    acknowledgement.transcriptReturned === false,
  );
}

function safeObservation(value: unknown, path: string): boolean {
  try {
    assertNoRawMaterial(value, path);
    return true;
  } catch {
    return false;
  }
}

function assertNoRawMaterial(value: unknown, path: string): void {
  if (Array.isArray(value)) {
    value.forEach((item, index) =>
      assertNoRawMaterial(item, `${path}[${index}]`),
    );
    return;
  }
  if (value === null || typeof value !== "object") return;
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    const normalized = key.trim().toLowerCase();
    if (RAW_MATERIAL_KEYS.has(normalized)) {
      throw new Error(
        `raw streaming voice material is not allowed at ${path}.${key}`,
      );
    }
    assertNoRawMaterial(item, `${path}.${key}`);
  }
}

function allContextPresent(
  context: PrenigmaContinuousVoiceProducerContext,
): context is Required<PrenigmaContinuousVoiceProducerContext> {
  return Boolean(
    safeText(context.productId) &&
    safeText(context.roomId) &&
    safeText(context.sessionId) &&
    safeText(context.tenantId) &&
    safeText(context.workspaceId) &&
    safeText(context.workflowId),
  );
}

function turnState(turns: Map<string, TurnState>, turnId: string): TurnState {
  const existing = turns.get(turnId);
  if (existing !== undefined) return existing;
  const created: TurnState = {};
  turns.set(turnId, created);
  return created;
}

function drainPending<T>(
  reader: (() => T[]) | undefined,
  handler: (value: T) => void,
): void {
  if (reader === undefined) return;
  try {
    for (const value of reader()) handler(value);
  } catch (error) {
    logger.warn("Prenigma continuous voice producer pending read failed", {
      error,
    });
  }
}

function subscribe<T>(
  subscriptions: Unsubscribe[],
  subscriber: ((handler: (value: T) => void) => Unsubscribe | void) | undefined,
  handler: (value: T) => void,
): void {
  if (subscriber === undefined) return;
  try {
    const unsubscribe = subscriber(handler);
    if (unsubscribe !== undefined) subscriptions.push(unsubscribe);
  } catch (error) {
    logger.warn("Prenigma continuous voice producer subscription failed", {
      error,
    });
  }
}

function acknowledge(
  host: PrenigmaContinuousVoiceProducerRuntimeHost | undefined,
  acknowledgement: PrenigmaContinuousVoiceProducerAcknowledgement,
): void {
  try {
    host?.acknowledgeContinuousVoiceProducerRequest?.(acknowledgement);
  } catch (error) {
    logger.warn("Prenigma continuous voice producer acknowledgement failed", {
      error,
      status: acknowledgement.status,
      turnId: acknowledgement.turnId,
    });
  }
}

function baseAcknowledgement(
  turnId: string,
): Omit<PrenigmaContinuousVoiceProducerAcknowledgement, "blockers" | "status"> {
  return {
    providerCredentialsReturned: false,
    rawAudioReturned: false,
    rawSecretMaterialReturned: false,
    rawTranscriptReturned: false,
    turnId,
  };
}

function safeText(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function safeRefSegment(value: string): string {
  const safe = value.trim().replace(/[^A-Za-z0-9_.:-]/g, "_");
  return safe || "turn";
}
